import { useState } from 'react';
import { useClusteringKeywords } from '../../../hooks/useClustering';
import type { KeywordsListParams } from '../../../types/clustering';
import { formatNumber } from '../shared';
import styles from './Results.module.css';

interface KeywordsTableProps {
  batchId: string;
  totalKeywords: number;
}

type KeywordSort = NonNullable<KeywordsListParams['sort']>;

const COLUMNS: { key: KeywordSort; label: string }[] = [
  { key: 'keyword', label: 'Keyword' },
  { key: 'search_volume', label: 'Volume' },
  { key: 'ads_competition', label: 'Ads competition' },
  { key: 'seo_difficulty', label: 'SEO difficulty' },
  { key: 'cpc', label: 'CPC' },
];

function cellFor(key: KeywordSort, row: {
  keyword: string;
  search_volume: number | null;
  google_ads_competition: number | null;
  seo_difficulty: number | null;
  cpc: number | null;
}) {
  switch (key) {
    case 'keyword':
      return row.keyword;
    case 'search_volume':
      return formatNumber(row.search_volume);
    case 'ads_competition':
      return formatNumber(row.google_ads_competition);
    case 'seo_difficulty':
      return formatNumber(row.seo_difficulty);
    case 'cpc':
      return row.cpc === null || row.cpc === undefined ? '—' : `$${row.cpc.toFixed(2)}`;
  }
}

export function KeywordsTable({ batchId, totalKeywords }: KeywordsTableProps) {
  const [params, setParams] = useState<KeywordsListParams>({
    sort: 'search_volume',
    order: 'desc',
    offset: 0,
  });

  const requestedLimit = Math.max(totalKeywords, 1);
  const { data: keywords = [], isLoading, error } = useClusteringKeywords(batchId, {
    ...params,
    limit: requestedLimit,
  });

  const toggleSort = (key: KeywordSort) => {
    setParams((prev) => ({
      ...prev,
      sort: key,
      order: prev.sort === key && prev.order === 'desc' ? 'asc' : 'desc',
      offset: 0,
    }));
  };

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className={styles['th--sortable']}
                onClick={() => toggleSort(c.key)}
              >
                {c.label}
                {params.sort === c.key && (
                  <span className={styles.sortArrow}>
                    {params.order === 'desc' ? '▼' : '▲'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {error && (
            <tr>
              <td colSpan={COLUMNS.length} className={styles.empty}>
                {(error as Error).message}
              </td>
            </tr>
          )}
          {isLoading ? (
            <tr>
              <td colSpan={COLUMNS.length} className={styles.empty}>
                Loading keywords…
              </td>
            </tr>
          ) : keywords.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className={styles.empty}>
                No keywords yet.
              </td>
            </tr>
          ) : (
            keywords.map((kw) => (
              <tr key={kw.id}>
                {COLUMNS.map((c) => (
                  <td key={c.key}>{cellFor(c.key, kw)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
